// Portfolio data — merged from both of Prashant Neupane's CVs
export const portfolioData = {
  hero: {
    firstName: "PRASHANT",
    lastName: "NEUPANE",
    role: "DevOps Engineer",
    tagline: "Cloud · Automation · Security",
    description:
      "DevOps engineer and AWS-certified Solutions Architect. I provision infrastructure as code, automate CI/CD, keep systems observable and secure — and teach the next wave of cloud & cybersecurity engineers.",
    location: "Kathmandu, Nepal",
    availability: "Available for opportunities",
  },

  // Terminal shown in the hero
  terminal: {
    prompt: "prashant@devops",
    lines: [
      { cmd: "kubectl get pods -n production" },
      { out: "NAME                     READY   STATUS    RESTARTS" },
      { out: "api-gateway-7d9f          1/1     Running   0" },
      { out: "worker-pool-3a2b          1/1     Running   0" },
      { out: "grafana-monitor-9c1e      1/1     Running   0" },
      { cmd: "terraform apply -auto-approve" },
      { out: "Apply complete! 0 to add, 0 to change, 0 to destroy." },
    ],
  },

  stats: [
    { value: "4+", label: "Years in DevOps" },
    { value: "AWS", label: "SA Associate certified" },
    { value: "3", label: "Cloud platforms" },
    { value: "24/7", label: "Uptime mindset" },
  ],

  // Scrolling pipeline / keyword ticker
  marquee: [
    "INFRASTRUCTURE AS CODE",
    "CI / CD",
    "KUBERNETES",
    "AWS",
    "TERRAFORM",
    "OBSERVABILITY",
    "DEVSECOPS",
    "DOCKER",
    "AUTOMATION",
  ],

  about: {
    bio: [
      "I'm a DevOps engineer at Deerhold Nepal, where I drive the design, automation, and optimization of build, deploy, and operations pipelines for scalable, reliable software delivery.",
      "I provision cloud resources as code, wire up CI/CD, and keep infrastructure observable with the ELK stack, Prometheus, and Grafana. In 2025 I earned my AWS Solutions Architect – Associate certification.",
      "Alongside engineering, I teach — AWS & DevOps at Deerwalk Compware and Cybersecurity at TechSpire College — turning hands-on production experience into labs on Linux, containers, pipelines, and secure architecture.",
    ],
    professional: ["Effective communication", "Team player", "Fast learner"],
  },

  experience: [
    {
      id: 1,
      role: "DevOps Engineer · Product DevOps Lead",
      company: "Deerhold Nepal",
      period: "Nov 2022 — Present",
      current: true,
      summary:
        "Drive the design, automation, and optimization of integration, deployment, and operations — ensuring scalable, efficient, and reliable software delivery.",
      highlights: [
        "Define and provision cloud resources as code with AWS CloudFormation, Terraform, Ansible, and Packer.",
        "Build automation in Bash and Python to streamline infrastructure management and operations.",
        "Run CI/CD with AWS CodePipeline, CodeBuild, CodeDeploy, Jenkins, and GitHub Actions.",
        "Design and manage APIs with AWS API Gateway, Lambda, and Flask.",
        "Automate big-data resources — Elasticsearch, MongoDB, MySQL, PostgreSQL, AWS Glue, EMR, and Airflow.",
        "Monitor infrastructure health with the ELK stack, Prometheus, Grafana, and Nagios.",
      ],
      keywords: ["IaC", "AWS", "Elasticsearch", "MongoDB", "CI/CD", "Glue", "EMR", "Python"],
    },
    {
      id: 2,
      role: "Cybersecurity Instructor",
      company: "TechSpire College",
      period: "Feb 2025 — Present",
      current: true,
      summary:
        "Teach core security principles and run live offensive/defensive demonstrations for students.",
      highlights: [
        "Teach the CIA triad, threat modeling, risk management, and OWASP Top 10 secure-coding principles.",
        "Explain firewalls, VPNs, IDS/IPS, and secure network architecture.",
        "Run live recon, scanning, and exploitation demos with Nmap, Metasploit, and Burp Suite.",
        "Demonstrate SIEM tooling — Splunk and the ELK stack — for threat monitoring and analysis.",
        "Design labs simulating phishing, brute-force attempts, and malware detection.",
      ],
      keywords: ["OWASP", "SIEM", "Splunk", "Nmap", "Metasploit", "Burp Suite"],
    },
    {
      id: 3,
      role: "AWS & DevOps Instructor",
      company: "Deerwalk Compware",
      period: "Jan 2024 — Present",
      current: true,
      summary:
        "Train students on Linux, cloud, containers, and pipelines with in-depth, hands-on sessions.",
      highlights: [
        "Teach Linux — shell scripting, file systems, user management, and system security.",
        "Train on CI/CD with Jenkins, GitLab CI/CD, and GitHub Actions.",
        "Instruct on Docker fundamentals, image creation, and container networking.",
        "Cover core AWS — EC2, S3, IAM, VPC, RDS, CloudFormation, Lambda, and CloudWatch.",
        "Implement monitoring with Prometheus, Grafana, CloudWatch, and the ELK stack.",
      ],
      keywords: ["AWS", "Linux", "Docker", "CI/CD", "VPC", "IAM"],
    },
    {
      id: 4,
      role: "DevOps Intern",
      company: "Deerwalk Institute of Technology",
      period: "Jan 2021 — Sep 2022",
      summary:
        "Automated provisioning and deployments while debugging and containerizing web applications.",
      highlights: [
        "Implemented IaC with Terraform and Ansible to automate provisioning.",
        "Set up CI/CD pipelines to streamline builds, testing, and deployments.",
        "Resolved application issues across the lifecycle for seamless production rollouts.",
        "Provisioned, monitored, and scaled EC2 instances in AWS.",
        "Integrated Docker to simplify workflows and improve deployment consistency.",
      ],
      keywords: ["IaC", "Terraform", "Ansible", "Docker", "AWS EC2"],
    },
  ],

  skills: [
    { category: "Cloud", items: ["AWS", "Azure", "Digital Ocean"] },
    { category: "IaC & Config", items: ["Terraform", "Ansible", "Packer", "CloudFormation", "SAM"] },
    { category: "CI / CD", items: ["Jenkins", "GitLab CI", "GitHub Actions", "CodePipeline"] },
    { category: "Containers & Orchestration", items: ["Docker", "Kubernetes", "ECS", "Virtualization"] },
    { category: "Observability", items: ["ELK", "Grafana", "Prometheus", "CloudWatch", "Nagios"] },
    { category: "Databases & Data", items: ["MongoDB", "RDS", "Elasticsearch", "PySpark", "Pandas", "FastAPI"] },
    { category: "Languages", items: ["Python", "Bash", "NodeJS", "React", "HTML/CSS"] },
    { category: "Security & DevSecOps", items: ["OWASP", "SIEM / Splunk", "Nmap", "Metasploit", "Burp Suite", "SSO / SAML", "HIPAA", "SOC2"] },
  ],

  education: [
    {
      degree: "BSc. Computer Science & Information Technology",
      school: "Deerwalk Institute of Technology · Tribhuvan University",
      period: "2018 — 2023",
    },
    {
      degree: "Science (+2)",
      school: "Prasadi Academy",
      period: "2016 — 2018",
    },
  ],

  certifications: [
    {
      title: "AWS Certified Solutions Architect – Associate",
      issuer: "Amazon Web Services",
      date: "Jan 2025",
    },
  ],

  awards: [
    { title: "Semester Topper Award", org: "Deerwalk Institute of Technology", date: "2023" },
    { title: "Club President", org: "Deerwalk Institute of Technology", date: "2021" },
  ],

  interests: ["Cricket", "Travelling", "Hiking"],

  contact: {
    email: "neupaneprashant22@gmail.com",
    phone: "+977 9861853849",
    location: "Kathmandu, Nepal",
    social: [
      { name: "GitHub", url: "https://github.com/neupaneprashant22", icon: "github" },
      { name: "LinkedIn", url: "https://www.linkedin.com/in/prashant-neupane-0422791a2/", icon: "linkedin" },
      { name: "Email", url: "mailto:neupaneprashant22@gmail.com", icon: "mail" },
    ],
  },
};
